import { useCallback, useEffect, useMemo } from 'react';

import { Controller, useController, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import Accordion from '@/components/atoms/Accordion/Accordion';
import { Avatar } from '@/components/atoms/Avatar/Avatar/Avatar';
import OptionRadioButton from '@/components/atoms/OptionRadioButton/OptionRadioButton';
import TitleSubtitle from '@/components/atoms/TitleSubtitle/TitleSubtitle';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useIntegrations } from '@/hooks/useIntegrations';
import usePrompt from '@/hooks/usePrompt';
import { setDirty, setExpanded } from '@/redux/integrations/actions';
import { selectAccordion } from '@/redux/integrations/selectors';
import { getPermissions } from '@/redux/permissions/selectors';
import { AccordionFieldsetProps, FrontIntegration } from '@/shared/types/integration';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { errorMessage, removeEmptyFields, trimOnBlur } from '@/util/validation/helpers';
import { frontIntegrationConfig } from '@/util/validation/rules/integration-rules';

type Form = { mode: 'agent' | 'system'; teammate_id: string; sender_name: string };

const FrontMode = ({
  type,
  integration,
  registerAccordion,
  toggleAccordion,
}: AccordionFieldsetProps<Form, FrontIntegration>) => {
  const { t } = useTranslation();

  const { updateIntegration, updateStatus } = useIntegrations<FrontIntegration>(
    integration?.desk_id,
    integration?.integration_id
  );
  const formMethods = useForm<Form>({ mode: 'onSubmit' });

  const teammates = integration?.config?.teammates;

  const { expanded } = useSelector(selectAccordion);
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'integrations', actions.WRITE)
  );

  const dispatch = useDispatch();

  const {
    reset,
    control,
    formState: { isDirty },
    handleSubmit,
    trigger,
  } = formMethods;

  const { field: mode } = useController({ name: 'mode', control });

  const { field: teammate_id } = useController({
    name: 'teammate_id',
    control,
    rules: frontIntegrationConfig.teammate_id,
  });

  useEffect(() => {
    reset({
      mode: integration?.config?.mode,
      sender_name: integration?.config?.sender_name || '',
      teammate_id: integration?.config?.teammate_id || '',
    });
  }, [
    reset,
    integration?.config?.mode,
    integration?.config?.teammate_id,
    integration?.config?.sender_name,
  ]);

  const handleUpdateIntegration = useCallback(
    (values: Form) => {
      const config = removeEmptyFields(values);
      updateIntegration(
        { ...integration, config: { ...integration.config, ...config } },
        {
          onSuccess: () => {
            if (expanded === type) {
              dispatch(setExpanded(false));
            }
          },
        }
      );
    },
    [dispatch, expanded, integration, type, updateIntegration]
  );

  usePrompt(isDirty, undefined, undefined, handleSubmit(handleUpdateIntegration));

  const onModeChange = useCallback((event) => mode.onChange(event.target.value), [mode]);

  const modeOptions = useMemo(
    () => [
      {
        id: 'agent-mode',
        value: 'agent',
        label: (
          <TitleSubtitle
            title={t('integrations.front.mode.agent.title')}
            subtitle={t('integrations.front.mode.agent.subtitle')}
            noGutters
          />
        ),
      },
      {
        id: 'system-mode',
        value: 'system',
        label: (
          <TitleSubtitle
            title={t('integrations.front.mode.system.title')}
            subtitle={t('integrations.front.mode.system.subtitle')}
            noGutters
          />
        ),
      },
    ],
    [t]
  );

  useEffect(() => {
    dispatch(setDirty(isDirty));
  }, [dispatch, isDirty]);

  useEffect(() => {
    if (expanded !== type && isDirty) {
      reset();
    }
  }, [expanded, isDirty, reset, type]);

  return (
    <>
      <Accordion
        title={t('integrations.front.mode.title')}
        subtitle={t('integrations.front.mode.subtitle')}
        onSubmit={handleSubmit(handleUpdateIntegration)}
        disabled={!isDirty}
        isLoading={updateStatus === 'pending'}
        expanded={expanded === type}
        handleChange={toggleAccordion(type)}
        ref={registerAccordion(type, handleSubmit(handleUpdateIntegration), trigger)}
        readOnly={!canWrite}
      >
        <div className="mb-6 ml-1 flex flex-col gap-2">
          <OptionRadioButton
            key={`${modeOptions[0].id}-${mode.value}`}
            option={modeOptions[0]}
            size="medium"
            onChange={onModeChange}
            isSelected={mode.value === 'agent'}
            disableSelectedStyles
            disabled={!canWrite}
          />
          <OptionRadioButton
            key={`${modeOptions[1].id}-${mode.value}`}
            option={modeOptions[1]}
            size="medium"
            onChange={onModeChange}
            isSelected={mode.value === 'system'}
            disableSelectedStyles
            disabled={!canWrite}
          />
        </div>
        {mode.value === 'system' && (
          // The error is read from fieldState: without a resolver, react-hook-form fills
          // `errors` in place on submit, and this conditional block would keep the old one.
          <Controller
            control={control}
            name="sender_name"
            rules={frontIntegrationConfig.sender_name}
            render={({ field, fieldState: { error } }) => {
              const senderNameError = errorMessage({ field: error });
              return (
                <>
                  <TitleSubtitle
                    title={t('integrations.front.sender_name.title')}
                    subtitle={t('integrations.front.sender_name.subtitle')}
                    error={!!error}
                  />
                  <Input
                    id="sender_name"
                    placeholder={t('integrations.front.sender_name.placeholder')}
                    aria-invalid={!!error}
                    readOnly={!canWrite}
                    {...field}
                    value={field.value ?? ''}
                    onBlur={trimOnBlur(field)}
                  />
                  {senderNameError && (
                    <p className="mt-1 text-sm text-warning">{senderNameError}</p>
                  )}
                </>
              );
            }}
          />
        )}
        {mode.value === 'agent' && (
          <>
            <TitleSubtitle
              title={t('integrations.front.teammate.title')}
              subtitle={t('integrations.front.teammate.subtitle')}
            />
            <Select
              value={teammate_id.value || ''}
              onValueChange={teammate_id.onChange}
              disabled={!canWrite}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={t('conversation.assign')} />
              </SelectTrigger>
              <SelectContent>
                {teammates?.map((teammate) => (
                  <SelectItem key={teammate.id} value={teammate.id}>
                    <Avatar size="small" userId={teammate.id} />
                    <span className="truncate">{teammate.name}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </>
        )}
      </Accordion>
    </>
  );
};

export default FrontMode;
